/**
 * ApiKeyModal — Reusable modal for API key creation, secret display, and management.
 * Used on both Custom Reports and Accounting Output Formats pages.
 * (KAN-93)
 *
 * Three states:
 *  1. CREATE  — form to create a new key for a template
 *  2. SECRET  — shows the plaintext secret once after creation (copy-gate)
 *  3. MANAGE  — inline metadata, preview, disable/enable, revoke
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { dataFeedService } from '../../services/api/dataFeedService';
import AlertModal from './AlertModal';
import type {
  ApiKeyDocument,
  ApiKeyCreateResponse,
  TemplateSource,
} from '../../types/apiKey';
import './ApiKeyModal.css';


// ============================================================
// Props
// ============================================================

interface ApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  templateId: string;
  templateName: string;
  templateSource: TemplateSource;
  /** If the template already has a key, pass it here to go straight to MANAGE. */
  existingKey?: ApiKeyDocument | null;
  /** Called after create / revoke / update so parent can refresh. */
  onKeyChanged: () => void;
}


// ============================================================
// Component
// ============================================================

const ApiKeyModal: React.FC<ApiKeyModalProps> = ({
  isOpen,
  onClose,
  clientId,
  templateId,
  templateName,
  templateSource,
  existingKey,
  onKeyChanged,
}) => {
  const { t } = useTranslation();

  // State machine
  type ModalView = 'CREATE' | 'SECRET' | 'MANAGE' | 'PREVIEW';
  const [view, setView] = useState<ModalView>(existingKey ? 'MANAGE' : 'CREATE');

  // Create form fields
  const [keyName, setKeyName] = useState('');
  const [keyDescription, setKeyDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Secret display
  const [createdKey, setCreatedKey] = useState<ApiKeyCreateResponse | null>(null);
  const [secretCopied, setSecretCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  // Manage state
  const [keyData, setKeyData] = useState<ApiKeyDocument | null>(existingKey || null);
  const [toggling, setToggling] = useState(false);

  // Preview state
  const [previewDateFrom, setPreviewDateFrom] = useState('');
  const [previewDateTo, setPreviewDateTo] = useState('');
  const [previewDateField, setPreviewDateField] = useState<'TradeDate' | 'MaturityDate'>('TradeDate');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRows, setPreviewRows] = useState<Record<string, any>[] | null>(null);
  const [previewTotalRows, setPreviewTotalRows] = useState(0);

  // Confirm dialog for revoke
  const [confirmRevoke, setConfirmRevoke] = useState(false);

  // Error
  const [error, setError] = useState<string | null>(null);

  // Snippet tab (curl vs Postman) in PREVIEW view
  const [snippetTab, setSnippetTab] = useState<'curl' | 'postman'>('curl');

  // Dirty flag — tracks whether changes were made (create / toggle / revoke)
  // so we can call onKeyChanged only when the modal closes.
  const dirtyRef = useRef(false);

  // Reset ALL modal state every time isOpen flips to true.
  // This fixes: (1) stale view when reopening with different existingKey,
  //             (2) leftover form data from a previous session.
  useEffect(() => {
    if (isOpen) {
      setView(existingKey ? 'MANAGE' : 'CREATE');
      setKeyName('');
      setKeyDescription('');
      setCreating(false);
      setCreatedKey(null);
      setSecretCopied(false);
      setUrlCopied(false);
      setKeyData(existingKey || null);
      setToggling(false);
      setPreviewDateFrom('');
      setPreviewDateTo('');
      setPreviewDateField('TradeDate');
      setPreviewLoading(false);
      setPreviewRows(null);
      setPreviewTotalRows(0);
      setSnippetTab('curl');
      setConfirmRevoke(false);
      setError(null);
      dirtyRef.current = false;
    }
  }, [isOpen, existingKey]);

  // ========== Actions ==========

  const handleCreate = useCallback(async () => {
    if (!keyName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const result = await dataFeedService.createKey(clientId, {
        name: keyName.trim(),
        description: keyDescription.trim() || undefined,
        templateId,
        templateSource,
      });

      setCreatedKey(result);
      setView('SECRET');
      dirtyRef.current = true;
    } catch (err: any) {
      setError(err.message || t('dataFeeds.errors.createFailed', 'Failed to create API key'));
    } finally {
      setCreating(false);
    }
  }, [keyName, keyDescription, clientId, templateId, templateSource, t]);

  const handleCopySecret = useCallback(async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.keySecret);
      setSecretCopied(true);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = createdKey.keySecret;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setSecretCopied(true);
    }
  }, [createdKey]);

  const handleCopyUrl = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    }
  }, []);

  const handleSecretDone = useCallback(() => {
    // Move to manage view with the newly created key
    if (createdKey) {
      setKeyData({
        keyId: createdKey.keyId,
        name: createdKey.name,
        description: createdKey.description,
        templateId: createdKey.templateId,
        templateSource: createdKey.templateSource,
        sourceType: createdKey.sourceType,
        templateName: createdKey.templateName,
        isActive: createdKey.isActive,
        rateLimitPerMinute: createdKey.rateLimitPerMinute || 10,
        allowedFormats: createdKey.allowedFormats,
        createdAt: createdKey.createdAt,
        createdBy: createdKey.createdBy,
        lastUsedAt: createdKey.lastUsedAt,
        callCount: createdKey.callCount,
        endpointUrl: createdKey.endpointUrl,
      });
    }
    setView('MANAGE');
  }, [createdKey]);

  const handleToggleActive = useCallback(async () => {
    if (!keyData) return;
    setToggling(true);
    setError(null);
    try {
      const updated = await dataFeedService.updateKey(clientId, keyData.keyId, {
        isActive: !keyData.isActive,
      });
      setKeyData(updated);
      dirtyRef.current = true;
    } catch (err: any) {
      setError(err.message || t('dataFeeds.errors.updateFailed', 'Failed to update API key'));
    } finally {
      setToggling(false);
    }
  }, [keyData, clientId, t]);

  const handleRevoke = useCallback(async () => {
    if (!keyData) return;
    setError(null);
    try {
      await dataFeedService.revokeKey(clientId, keyData.keyId);
      dirtyRef.current = false; // prevent double-call via handleClose
      onKeyChanged();
      onClose();
    } catch (err: any) {
      setError(err.message || t('dataFeeds.errors.revokeFailed', 'Failed to revoke API key'));
    }
    setConfirmRevoke(false);
  }, [keyData, clientId, onKeyChanged, onClose, t]);

  const handlePreview = useCallback(async () => {
    if (!keyData) return;
    setPreviewLoading(true);
    setError(null);
    setPreviewRows(null);
    try {
      const params: Record<string, string> = {};
      if (previewDateFrom) params.dateFrom = previewDateFrom;
      if (previewDateTo) params.dateTo = previewDateTo;
      if (previewDateField) params.dateField = previewDateField;

      const result = await dataFeedService.previewKey(clientId, keyData.keyId, params);
      setPreviewRows(result.rows || []);
      setPreviewTotalRows(result.totalRows || 0);
    } catch (err: any) {
      setError(err.message || t('dataFeeds.errors.previewFailed', 'Preview failed'));
    } finally {
      setPreviewLoading(false);
    }
  }, [keyData, clientId, previewDateFrom, previewDateTo, previewDateField, t]);

  // ========== Helpers ==========

  const formatDate = (iso?: string) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${mins}`;
    } catch {
      return iso;
    }
  };

  /** Close the modal and notify the parent if any changes were made. */
  const handleClose = useCallback(() => {
    if (dirtyRef.current) {
      onKeyChanged();
    }
    onClose();
  }, [onClose, onKeyChanged]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      // Don't allow closing on SECRET view
      if (view !== 'SECRET') handleClose();
    }
  };

  // ========== Render ==========

  if (!isOpen) return null;

  return (
    <>
      <div className="akm-backdrop" onClick={handleBackdropClick}>
        <div className="akm-modal">

          {/* ========== CREATE VIEW ========== */}
          {view === 'CREATE' && (
            <>
              <div className="akm-header">
                <h3>{t('dataFeeds.modal.createTitle', 'Generate API Key')}</h3>
                <button className="akm-close" onClick={handleClose}>&times;</button>
              </div>
              <div className="akm-template-label">
                {t('dataFeeds.modal.template', 'Template')}: <strong>{templateName}</strong>
              </div>
              <div className="akm-body">
                <div className="akm-field">
                  <label>{t('dataFeeds.modal.name', 'Name')} *</label>
                  <input
                    type="text"
                    value={keyName}
                    onChange={e => setKeyName(e.target.value)}
                    placeholder={t('dataFeeds.modal.namePlaceholder', 'e.g. SAP TMS — Live Trades')}
                    maxLength={100}
                    autoFocus
                  />
                </div>
                <div className="akm-field">
                  <label>{t('dataFeeds.modal.description', 'Description')} ({t('common.optional', 'optional')})</label>
                  <input
                    type="text"
                    value={keyDescription}
                    onChange={e => setKeyDescription(e.target.value)}
                    placeholder={t('dataFeeds.modal.descriptionPlaceholder', 'e.g. USD/CLP forwards for SAP treasury module')}
                    maxLength={500}
                  />
                </div>
                {error && <div className="akm-error">{error}</div>}
              </div>
              <div className="akm-footer">
                <button className="akm-btn" onClick={handleClose}>
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  className="akm-btn akm-btn-primary"
                  onClick={handleCreate}
                  disabled={creating || !keyName.trim()}
                >
                  {creating
                    ? t('dataFeeds.modal.creating', 'Generating...')
                    : t('dataFeeds.modal.generateKey', 'Generate Key')}
                </button>
              </div>
            </>
          )}

          {/* ========== SECRET VIEW ========== */}
          {view === 'SECRET' && createdKey && (
            <>
              <div className="akm-header">
                <h3>{t('dataFeeds.modal.secretTitle', 'API Key Generated')}</h3>
              </div>
              <div className="akm-body">
                <p className="akm-key-name">{createdKey.name}</p>

                <div className="akm-secret-section">
                  <label>{t('dataFeeds.modal.yourSecret', 'Your API key secret')}:</label>
                  <div className="akm-secret-row">
                    <code className="akm-secret-value">{createdKey.keySecret}</code>
                    <button
                      className="akm-btn akm-btn-small"
                      onClick={handleCopySecret}
                    >
                      {secretCopied
                        ? t('dataFeeds.modal.copied', 'Copied!')
                        : t('dataFeeds.modal.copy', 'Copy')}
                    </button>
                  </div>
                </div>

                <div className="akm-warning">
                  {t('dataFeeds.modal.secretWarning', 'This key will not be shown again. Copy it now and store it in a secrets manager or password vault. Anyone with this key can read your data.')}
                </div>

                <div className="akm-secret-section">
                  <label>{t('dataFeeds.modal.endpointUrl', 'Endpoint URL')}:</label>
                  <div className="akm-secret-row">
                    <code className="akm-secret-value akm-url-value">{createdKey.endpointUrl}</code>
                    <button
                      className="akm-btn akm-btn-small"
                      onClick={() => handleCopyUrl(createdKey.endpointUrl)}
                    >
                      {urlCopied
                        ? t('dataFeeds.modal.copied', 'Copied!')
                        : t('dataFeeds.modal.copyUrl', 'Copy URL')}
                    </button>
                  </div>
                </div>

                {error && <div className="akm-error">{error}</div>}
              </div>
              <div className="akm-footer">
                <button
                  className="akm-btn akm-btn-primary"
                  onClick={handleSecretDone}
                  disabled={!secretCopied}
                >
                  {t('dataFeeds.modal.doneCopied', 'I have copied my key — Done')}
                </button>
              </div>
            </>
          )}

          {/* ========== MANAGE VIEW ========== */}
          {view === 'MANAGE' && keyData && (
            <>
              <div className="akm-header">
                <h3>{t('dataFeeds.modal.manageTitle', 'API Key')}</h3>
                <button className="akm-close" onClick={handleClose}>&times;</button>
              </div>
              <div className="akm-body">
                <div className="akm-manage-row">
                  <span className="akm-manage-name">{keyData.name}</span>
                  <span className={`akm-status-badge ${keyData.isActive ? 'active' : 'disabled'}`}>
                    {keyData.isActive
                      ? t('dataFeeds.status.active', 'Active')
                      : t('dataFeeds.status.disabled', 'Disabled')}
                  </span>
                </div>

                {keyData.description && (
                  <p className="akm-manage-desc">{keyData.description}</p>
                )}

                <div className="akm-meta-grid">
                  <div className="akm-meta-item">
                    <span className="akm-meta-label">{t('dataFeeds.meta.lastUsed', 'Last used')}</span>
                    <span className="akm-meta-value">{formatDate(keyData.lastUsedAt)}</span>
                  </div>
                  <div className="akm-meta-item">
                    <span className="akm-meta-label">{t('dataFeeds.meta.calls', 'Calls')}</span>
                    <span className="akm-meta-value">{keyData.callCount}</span>
                  </div>
                  <div className="akm-meta-item">
                    <span className="akm-meta-label">{t('dataFeeds.meta.created', 'Created')}</span>
                    <span className="akm-meta-value">{formatDate(keyData.createdAt)}</span>
                  </div>
                  <div className="akm-meta-item">
                    <span className="akm-meta-label">{t('dataFeeds.meta.formats', 'Formats')}</span>
                    <span className="akm-meta-value">
                      {keyData.allowedFormats.length > 0 ? keyData.allowedFormats.join(', ') : t('dataFeeds.meta.allFormats', 'All')}
                    </span>
                  </div>
                </div>

                <div className="akm-endpoint-section">
                  <label>{t('dataFeeds.modal.endpointUrl', 'Endpoint URL')}:</label>
                  <div className="akm-secret-row">
                    <code className="akm-secret-value akm-url-value">{keyData.endpointUrl}</code>
                    <button
                      className="akm-btn akm-btn-small"
                      onClick={() => handleCopyUrl(keyData.endpointUrl)}
                    >
                      {urlCopied
                        ? t('dataFeeds.modal.copied', 'Copied!')
                        : t('dataFeeds.modal.copyUrl', 'Copy URL')}
                    </button>
                  </div>
                </div>

                {error && <div className="akm-error">{error}</div>}
              </div>
              <div className="akm-footer">
                <button
                  className="akm-btn"
                  onClick={() => { setPreviewRows(null); setView('PREVIEW'); }}
                >
                  {t('dataFeeds.actions.preview', 'Preview')}
                </button>
                <button
                  className="akm-btn"
                  onClick={handleToggleActive}
                  disabled={toggling}
                >
                  {toggling
                    ? '...'
                    : keyData.isActive
                      ? t('dataFeeds.actions.disable', 'Disable')
                      : t('dataFeeds.actions.enable', 'Enable')}
                </button>
                <button
                  className="akm-btn akm-btn-danger"
                  onClick={() => setConfirmRevoke(true)}
                >
                  {t('dataFeeds.actions.revoke', 'Revoke')}
                </button>
                <button
                  className="akm-btn akm-btn-primary"
                  onClick={handleClose}
                >
                  {t('common.ok', 'OK')}
                </button>
              </div>
            </>
          )}

          {/* ========== PREVIEW VIEW ========== */}
          {view === 'PREVIEW' && keyData && (
            <>
              <div className="akm-header">
                <h3>{t('dataFeeds.preview.title', 'Preview')}: {keyData.name}</h3>
                <button className="akm-close" onClick={() => setView('MANAGE')}>&times;</button>
              </div>
              <div className="akm-body">
                <div className="akm-template-label">
                  {t('dataFeeds.modal.template', 'Template')}: <strong>{templateName}</strong>
                </div>

                <div className="akm-preview-controls">
                  <div className="akm-field akm-field-inline">
                    <label>{t('dataFeeds.preview.dateFrom', 'From')}</label>
                    <input
                      type="date"
                      value={previewDateFrom}
                      onChange={e => setPreviewDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="akm-field akm-field-inline">
                    <label>{t('dataFeeds.preview.dateTo', 'To')}</label>
                    <input
                      type="date"
                      value={previewDateTo}
                      onChange={e => setPreviewDateTo(e.target.value)}
                    />
                  </div>
                  <div className="akm-field akm-field-inline">
                    <label>{t('dataFeeds.preview.dateField', 'Field')}</label>
                    <select
                      value={previewDateField}
                      onChange={e => setPreviewDateField(e.target.value as 'TradeDate' | 'MaturityDate')}
                    >
                      <option value="TradeDate">{t('dataFeeds.preview.tradeDate', 'Trade Date')}</option>
                      <option value="MaturityDate">{t('dataFeeds.preview.maturityDate', 'Maturity Date')}</option>
                    </select>
                  </div>
                  <button
                    className="akm-btn akm-btn-primary"
                    onClick={handlePreview}
                    disabled={previewLoading}
                  >
                    {previewLoading
                      ? t('dataFeeds.preview.loading', 'Loading...')
                      : t('dataFeeds.preview.run', 'Run Preview')}
                  </button>
                </div>

                {/* Sample snippets (curl / Postman) */}
                {(() => {
                  const params = new URLSearchParams();
                  if (previewDateFrom) params.set('dateFrom', previewDateFrom);
                  if (previewDateTo) params.set('dateTo', previewDateTo);
                  if (previewDateField !== 'TradeDate') params.set('dateField', previewDateField);
                  params.set('format', 'json');
                  const qs = params.toString();
                  const curlUrl = `${keyData.endpointUrl}${qs ? `?${qs}` : ''}`;
                  const curlCmd = `curl -H "Authorization: ApiKey YOUR_SECRET" "${curlUrl}"`;

                  return (
                    <div className="akm-curl-section">
                      <div className="akm-curl-header">
                        <div className="akm-snippet-tabs">
                          <button
                            className={`akm-snippet-tab ${snippetTab === 'curl' ? 'active' : ''}`}
                            onClick={() => setSnippetTab('curl')}
                          >
                            curl
                          </button>
                          <button
                            className={`akm-snippet-tab ${snippetTab === 'postman' ? 'active' : ''}`}
                            onClick={() => setSnippetTab('postman')}
                          >
                            Postman
                          </button>
                        </div>
                        {snippetTab === 'curl' && (
                          <button
                            className="akm-btn akm-btn-small"
                            onClick={() => handleCopyUrl(curlCmd)}
                          >
                            {urlCopied
                              ? t('dataFeeds.modal.copied', 'Copied!')
                              : t('dataFeeds.modal.copy', 'Copy')}
                          </button>
                        )}
                      </div>
                      {snippetTab === 'curl' && (
                        <pre className="akm-curl-code">{curlCmd}</pre>
                      )}
                      {snippetTab === 'postman' && (
                        <div className="akm-postman-steps">
                          <table className="akm-postman-table">
                            <tbody>
                              <tr>
                                <td className="akm-postman-label">{t('dataFeeds.postman.method', 'Method')}</td>
                                <td><code>GET</code></td>
                              </tr>
                              <tr>
                                <td className="akm-postman-label">{t('dataFeeds.postman.url', 'URL')}</td>
                                <td>
                                  <code className="akm-postman-url">{curlUrl}</code>
                                  <button
                                    className="akm-btn akm-btn-small akm-postman-copy"
                                    onClick={() => handleCopyUrl(curlUrl)}
                                  >
                                    {urlCopied
                                      ? t('dataFeeds.modal.copied', 'Copied!')
                                      : t('dataFeeds.modal.copy', 'Copy')}
                                  </button>
                                </td>
                              </tr>
                              <tr>
                                <td className="akm-postman-label">{t('dataFeeds.postman.auth', 'Auth')}</td>
                                <td><code>{t('dataFeeds.postman.noAuth', 'No Auth')}</code></td>
                              </tr>
                              <tr>
                                <td className="akm-postman-label">{t('dataFeeds.postman.header', 'Header')}</td>
                                <td><code>Authorization</code> = <code>ApiKey YOUR_SECRET</code></td>
                              </tr>
                            </tbody>
                          </table>
                          <p className="akm-postman-hint">
                            {t('dataFeeds.preview.postmanHint', 'Create a new GET request in Postman, paste the URL, set Auth to "No Auth", then add the Authorization header with your API key secret.')}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {previewRows !== null && (
                  <div className="akm-preview-results">
                    <div className="akm-preview-summary">
                      {previewTotalRows} {t('dataFeeds.preview.totalRows', 'total rows')}
                      {previewRows.length < previewTotalRows && (
                        <> | {t('dataFeeds.preview.truncated', 'truncated to {{count}}', { count: previewRows.length })}</>
                      )}
                    </div>
                    <div className="akm-preview-json">
                      <pre>{JSON.stringify(previewRows, null, 2)}</pre>
                    </div>
                  </div>
                )}

                {error && <div className="akm-error">{error}</div>}
              </div>
              <div className="akm-footer">
                <button className="akm-btn" onClick={() => setView('MANAGE')}>
                  {t('common.back', 'Back')}
                </button>
              </div>
            </>
          )}

        </div>
      </div>

      {/* Revoke confirmation */}
      <AlertModal
        isOpen={confirmRevoke}
        type="warning"
        title={t('dataFeeds.revoke.title', 'Revoke API Key')}
        message={t('dataFeeds.revoke.confirm', 'Are you sure you want to revoke "{{name}}"? This will permanently delete the key. External systems using this key will immediately lose access. This cannot be undone.', { name: keyData?.name || '' })}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={handleRevoke}
        confirmLabel={t('dataFeeds.actions.revoke', 'Revoke')}
      />
    </>
  );
};

export default ApiKeyModal;
